import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "../../../apiClient";
import {
    UpdateVenueAgentCameraAssignmentsInputDto,
    VenueAgentDto,
} from "../../../models/court-recording/dto/venue-agent.dto";
import { GET_VENUE_AGENTS_QUERY_KEY } from "./useListVenueAgents";

/**
 * Replaces an agent's admin-set camera→court assignments
 * (`{ "<camera MAC>": courtId }`). The box applies the mapping to its own
 * config on its next heartbeat and restarts, so the change shows as
 * "pending" in the panel for up to about a minute.
 *
 * Sending `{}` clears every assignment; the box then keeps whatever mapping
 * its config already has (nothing is removed on the box).
 */
export const useUpdateVenueAgentCameraAssignments = (organizationId: string) => {
    const client = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            agentId,
            assignments,
        }: {
            agentId: string;
            assignments: Record<string, number>;
        }) =>
            client.fetchApi<VenueAgentDto, UpdateVenueAgentCameraAssignmentsInputDto>({
                endpoint: `court-recordings/admin/venue-agents/${agentId}/camera-assignments?organizationId=${organizationId}`,
                method: 'PATCH',
                body: { assignments },
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: GET_VENUE_AGENTS_QUERY_KEY(organizationId),
            });
        },
    });
};
